

import {
    Component,
    inject,
    input,
    OnDestroy,
    OnInit,
    SecurityContext,
    viewChild,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { MarkdownComponent, MarkdownService, provideMarkdown } from 'ngx-markdown';

import { hasValue } from '@my-aon/utils';
import { VendorsModule } from '@my-aon/vendors';
import { LoadIndicatorComponent } from '../load-indicator/load-indicator.component';
import { DxSplitterComponent } from 'devextreme-angular';
import { DocumentationType, DocumentationViewerService } from './documentation-viewer.service';
import { ScrollViewComponent } from '../scroll-view/scroll-view.component';



@Component({
    selector: 'app-documentation-viewer',
    templateUrl: './documentation-viewer.component.html',
    styleUrl: './documentation-viewer.component.scss',
    imports: [
        VendorsModule,
        MarkdownComponent,
        LoadIndicatorComponent,
        ScrollViewComponent
    ],
    providers: [
        provideMarkdown({ sanitize: SecurityContext.NONE })
    ],

})
export class DocumentationViewerComponent implements OnInit, OnDestroy {


    private readonly router = inject(Router);

    private readonly markdownService = inject(MarkdownService);
    readonly dvService = inject(DocumentationViewerService);

    readonly $markdownView = viewChild(MarkdownComponent);
    readonly $splitterView = viewChild(DxSplitterComponent);

    $type = input<DocumentationType>('general', { alias: 'type' });

    public markdown: string | null = null;
    public toc: { id: string; level: number; text: string | null }[] = [];

    public loadIndicatorVisible: boolean = true;
    public downloadInProgress: boolean = false;

    private readonly resourceHandlerMap: Map<string, string | null> = new Map();

    constructor() {

        this.router.events
            .pipe(takeUntilDestroyed())
            .subscribe((val) => {
                if (val instanceof NavigationEnd) {
                    let fragmentIdx = val.urlAfterRedirects.lastIndexOf('#');
                    if (
                        fragmentIdx >= 0 &&
                        fragmentIdx < val.urlAfterRedirects.length - 1
                    ) {
                        let fragment = val.urlAfterRedirects.substring(
                            fragmentIdx + 1
                        );
                        document.getElementById(fragment)?.scrollIntoView();
                    }
                }
            });
    }

    ngOnInit(): void {


        // Replace baseurl's for images
        const loadImg = (href: string, handlerId: string) => {
            this.resourceHandlerMap.set(handlerId, null);

            this.dvService.loadResource(this.$type(), href)
                .subscribe(
                    async (res) => {
                        if (!res) return;

                        while (this.resourceHandlerMap.get(handlerId) == null) {
                            const resourceElement = document.querySelector(
                                '.' + handlerId
                            );
                            if (resourceElement == null) {
                                await new Promise<void>((resolve) =>
                                    setTimeout(() => resolve(), 500)
                                );
                                continue;
                            }

                            const blobUrl = URL.createObjectURL(res);
                            this.resourceHandlerMap.set(handlerId, blobUrl);
                            if ('src' in resourceElement) {
                                resourceElement['src'] = blobUrl;
                            }
                        }
                    }
                );
        };

        this.markdownService.renderer.image = ({ href, title, text, tokens }) => {
            const handlerId = this.getNewHandlerId();
            loadImg(href, handlerId);
            const hint = title ?? text;
            return `<img class="${handlerId}" alt="${hint}" title="${hint}" width="100%"/>`;
        };

        this.markdownService.renderer.html = ({ text }) => {
            const template = document.createElement('template');
            template.innerHTML = text;
            const elem = template.content.firstChild!;
            if (!(elem instanceof HTMLElement)) return text;

            const processNodes = (node: Node) => {
                if (node.nodeName.toUpperCase() == 'IMG') {
                    const img = node as HTMLImageElement;
                    const handlerId = this.getNewHandlerId();
                    loadImg(img.src, handlerId);
                    img.classList.add(handlerId);
                    img.src = '';
                }
                for (var i = 0; i < node.childNodes.length; i++) {
                    var child = node.childNodes[i];
                    processNodes(child);
                }
            };

            processNodes(elem);
            elem.classList.add("from-md");
            return (elem as HTMLElement).outerHTML;
        };

        this.markdownService.reload();
        this.dvService.loadMarkdown(this.$type())
        .subscribe({
            next: (res) => {
                this.markdown = res;
                this.loadIndicatorVisible = false;
            },
            error: (err) => {
                console.error('Failed to load markdown:', err);
            }
        });
    }

    ngOnDestroy(): void {
        this.resourceHandlerMap.forEach(
            (x) => hasValue(x) && URL.revokeObjectURL(x)
        );
    }

    downloadPdf(): void {
        if (this.downloadInProgress) return;

        this.downloadInProgress = true;
        this.dvService.downloadPdf(this.$type(), () => {
            this.downloadInProgress = false;
        });
    }

    private getNewHandlerId() {
        return `md-res-${Date.now().toString(36) +
            Math.floor(
                Math.pow(10, 12) + Math.random() * 9 * Math.pow(10, 12)
            ).toString(36)
            }`;
    }

    onMarkdownReady(): void {
        // Generate TOC
        const comp = this.$markdownView()!;
        const element = comp?.viewContainerRef.element
            .nativeElement as HTMLElement;
        const headers = element.querySelectorAll('h1, h2, h3, h4, h5, h6');
        this.toc = Array.from(headers).map((header) => {
            const id = header!
                .textContent!.trim()
                .toLowerCase()
                .replace(/\s+/g, '-');
            header.setAttribute('id', id);
            const level = parseInt(header.tagName.substring(1), 10);
            return { id, text: header.textContent, level };
        });

        // Set splitter height - This will probably make the component less flexible but works wonders for current use.
        const splitterView = this.$splitterView()! as any;
        const mainElem = document.getElementsByTagName('main').item(0)!;
        splitterView.instance.element().style.maxHeight = mainElem.style.minHeight;
    }
}
